/**
 * Shapes the organizer menu for the side panel (mock `_kit/orgside.html`) from the visible groups of
 * `EventHub/settings/eventMenu.items.js` — the single source of entries, hrefs and gates.
 *
 * Presentation-only transforms:
 *  - "Athlete contract" + "Volunteer contract" become one "Contracts" group with two titled,
 *    collapsible sections (William, 29 Sept 2026).
 *  - every group gets an icon key (orgMenuIcons.jsx) and a stable `key`.
 */
export const CONTRACT_GROUPS = { 'Athlete contract': 'Athletes', 'Volunteer contract': 'Volunteers' }

export const MENU_ICON_BY_TITLE = {
    'Event page': 'eventPage',
    Athletes: 'registrations',
    Volunteers: 'volunteers',
    Spectators: 'spectators',
    Emailing: 'emailing',
    Planning: 'planning',
    'Live Judging': 'liveJudging',
    Leaderboard: 'leaderboard',
    Qualification: 'qualification',
    'Check-in': 'checkIn',
    Media: 'media',
    Contracts: 'contracts',
    Reviews: 'reviews',
}

const childOf = (child) =>
    child.invite
        ? { key: `invite:${child.invite}`, invite: child.invite }
        : { key: child.to, title: child.title, to: child.to }

export function buildOrgMenu(visibleItems = []) {
    const groups = []
    let contracts = null
    visibleItems.forEach((item) => {
        const section = CONTRACT_GROUPS[item.title]
        if (section) {
            if (!contracts) {
                contracts = { key: 'contracts', title: 'Contracts', icon: 'contracts', sections: [] }
                groups.push(contracts)
            }
            contracts.sections.push({ key: section, title: section, childs: (item.childs || []).map(childOf) })
            return
        }
        const group = { key: item.to ?? item.title, title: item.title, icon: MENU_ICON_BY_TITLE[item.title] }
        if (item.childs) group.childs = item.childs.map(childOf)
        else group.to = item.to
        groups.push(group)
    })
    return groups
}

/** Every link entry of a group (its own `to`, its childs, its sections' childs). */
export const groupEntries = (group) => {
    if (group.to !== undefined) return [group]
    if (group.childs) return group.childs.filter((child) => child.to !== undefined)
    if (group.sections) return group.sections.flatMap((section) => section.childs.filter((c) => c.to !== undefined))
    return []
}

/** First letters of the first two words of the name (mock `initialsOf`), "SF" when empty. */
export const initialsOf = (name = '') =>
    (String(name).match(/[\p{L}\p{N}]+/gu) || [])
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase() || 'SF'
